/* Header – Compact: mega menu, "More" overflow, mobile drawer, search hook.
   Scoped per section instance; every listener is bound with an AbortController
   so Theme Editor section reloads never double-bind. */
(() => {
  if (document.documentElement.hasAttribute('data-hc-js')) return;
  document.documentElement.setAttribute('data-hc-js', '');
  const DESKTOP = '(min-width: 1025px)';
  const HOVER_OPEN_MS = 150;
  const HOVER_CLOSE_MS = 200;
  const instances = new Map();

  const isDesktop = () => window.matchMedia(DESKTOP).matches;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function init(root) {
    const id = root.dataset.sectionId;
    if (instances.has(id)) destroy(id);

    const controller = new AbortController();
    const { signal } = controller;
    const on = (el, type, fn, opts) => el && el.addEventListener(type, fn, Object.assign({ signal }, opts));
    const sectionEl = root.closest('.shopify-section') || root;
    const header = root.querySelector('[data-hc-header]');

    /* ---- Disclosures (mega panel + More dropdown) ------------------------- */
    const disclosures = Array.from(root.querySelectorAll('[data-hc-disclosure]')).map((button) => {
      const item = button.closest('li');
      const panel = root.querySelector('#' + CSS.escape(button.getAttribute('aria-controls')));
      return { button, item, panel, timer: null, viaHover: false };
    }).filter((d) => d.panel);

    const within = (d, node) => node && (d.item.contains(node) || d.panel.contains(node));

    const close = (d, restoreFocus) => {
      clearTimeout(d.timer);
      if (d.button.getAttribute('aria-expanded') !== 'true') return;
      d.button.setAttribute('aria-expanded', 'false');
      d.panel.classList.remove('is-open');
      if (!d.panel.hasAttribute('data-hc-mega')) d.panel.hidden = true;
      if (restoreFocus) d.button.focus();
    };
    const closeAll = (except) => disclosures.forEach((d) => d !== except && close(d));
    const open = (d, viaHover = false) => {
      clearTimeout(d.timer);
      d.viaHover = viaHover;
      if (d.button.getAttribute('aria-expanded') === 'true') return;
      closeAll(d);
      d.panel.hidden = false;
      // Force a frame so the visibility/opacity transition runs from the hidden state.
      requestAnimationFrame(() => d.panel.classList.add('is-open'));
      d.button.setAttribute('aria-expanded', 'true');
    };

    disclosures.forEach((d) => {
      on(d.button, 'click', () => {
        const expanded = d.button.getAttribute('aria-expanded') === 'true';
        if (expanded && d.viaHover) { d.viaHover = false; return; }
        expanded ? close(d) : open(d);
      });
      // Closing on focusout covers Tab leaving the panel; the mega panel is a sibling of the
      // nav list, so both regions are watched.
      [d.item, d.panel].forEach((region) => on(region, 'focusout', (e) => {
        if (!within(d, e.relatedTarget)) close(d);
      }));
      if (d.item.hasAttribute('data-hc-hover') && canHover()) {
        [d.item, d.panel].forEach((region) => {
          on(region, 'mouseenter', () => {
            clearTimeout(d.timer);
            if (d.button.getAttribute('aria-expanded') !== 'true') d.timer = setTimeout(() => open(d, true), HOVER_OPEN_MS);
          });
          on(region, 'mouseleave', () => {
            clearTimeout(d.timer);
            d.timer = setTimeout(() => close(d), HOVER_CLOSE_MS);
          });
        });
      }
    });

    on(document, 'click', (e) => {
      disclosures.forEach((d) => { if (!within(d, e.target)) close(d); });
    });

    /* ---- "More" overflow --------------------------------------------------- */
    const nav = root.querySelector('[data-hc-nav]');
    const more = root.querySelector('[data-hc-more]');
    let resizeObserver = null;
    if (nav && more) {
      const list = root.querySelector('[data-hc-nav-list]');
      const morePanel = root.querySelector('[data-hc-more-panel]');
      const movable = Array.from(list.querySelectorAll(':scope > [data-hc-nav-item]:not([data-hc-mega-item]):not([data-hc-more]):not([data-hc-sub])'));
      const overflows = () => list.scrollWidth > list.clientWidth + 1;
      let lastWidth = -1;
      let queued = false;
      const fit = () => {
        queued = false;
        while (morePanel.firstElementChild) list.insertBefore(morePanel.firstElementChild, more);
        more.hidden = true;
        if (!isDesktop() || !overflows()) return;
        more.hidden = false;
        const inBar = movable.filter((li) => li.parentElement === list);
        while (overflows() && inBar.length) morePanel.prepend(inBar.pop());
      };
      const schedule = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(fit);
      };
      resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0].contentRect.width;
        if (width === lastWidth) return;
        lastWidth = width;
        schedule();
      });
      resizeObserver.observe(nav);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
      schedule();
    }

    /* ---- Mobile drawer ----------------------------------------------------- */
    const drawer = root.querySelector('[data-hc-drawer]');
    const overlay = root.querySelector('[data-hc-overlay]');
    const openBtn = root.querySelector('[data-hc-drawer-open]');
    const closeBtn = root.querySelector('[data-hc-drawer-close]');
    const inertTargets = () => Array.from(document.querySelectorAll('#MainContent, .wrapper-footer, .shopify-section-group-header-group'))
      .filter((el) => !el.contains(root));
    const drawerIsOpen = () => drawer && drawer.classList.contains('is-open');

    const openDrawer = () => {
      if (!drawer || drawerIsOpen()) return;
      closeAll();
      drawer.inert = false;
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.classList.add('hc-drawer-open');
      openBtn.setAttribute('aria-expanded', 'true');
      inertTargets().forEach((el) => { el.inert = true; });
      if (typeof trapFocus === 'function') trapFocus(drawer, closeBtn);
      else closeBtn.focus();
    };
    const closeDrawer = (restoreFocus = true) => {
      if (!drawerIsOpen()) return;
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.classList.remove('hc-drawer-open');
      openBtn.setAttribute('aria-expanded', 'false');
      inertTargets().forEach((el) => { el.inert = false; });
      drawer.inert = true;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? openBtn : null);
      else if (restoreFocus) openBtn.focus();
    };

    on(openBtn, 'click', openDrawer);
    on(closeBtn, 'click', () => closeDrawer());
    on(overlay, 'click', () => closeDrawer());

    // Accordions (drawer): one delegated listener.
    on(drawer, 'click', (e) => {
      const btn = e.target.closest('[data-hc-acc]');
      if (!btn) return;
      const panel = root.querySelector('#' + CSS.escape(btn.getAttribute('aria-controls')));
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      if (panel) panel.hidden = expanded;
    });

    /* ---- Search: reuse the theme's predictive-search sidebar --------------- */
    const searchBtn = root.querySelector('[data-hc-search]');
    const searchSidebar = document.getElementById('search-form-mobile');
    const searchIsOpen = () => document.body.classList.contains('open_search_mobile');
    const closeSearch = () => {
      if (!searchIsOpen()) return;
      document.body.classList.remove('open_search_mobile');
      if (searchBtn) searchBtn.setAttribute('aria-expanded', 'false');
    };
    if (searchBtn && searchSidebar) {
      on(searchBtn, 'click', () => {
        closeDrawer(false);
        closeAll();
        document.body.classList.add('open_search_mobile');
        searchBtn.setAttribute('aria-expanded', 'true');
        const input = searchSidebar.querySelector('input[type="search"]');
        if (input) setTimeout(() => input.focus(), 300);
      });
      // theme.js only wires these closers below 1025px; cover desktop too.
      on(document, 'click', (e) => {
        if (!searchIsOpen()) return;
        if (e.target.closest('[data-search-close-sidebar]') || e.target.classList.contains('background-overlay')) {
          e.preventDefault();
          closeSearch();
          searchBtn.focus();
        }
      });
    } else if (searchBtn) {
      // No sidebar in the layout (e.g. stripped template): fall back to the search page.
      on(searchBtn, 'click', () => { window.location.href = (window.routes && window.routes.search_url) || '/search'; });
    }

    /* ---- Escape --------------------------------------------------------------- */
    on(document, 'keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (drawerIsOpen()) { closeDrawer(); return; }
      if (searchIsOpen() && searchBtn) { closeSearch(); searchBtn.focus(); return; }
      disclosures.forEach((d) => close(d, within(d, document.activeElement)));
    });

    /* ---- Badges: hide when the count drops to zero --------------------------- */
    const badges = root.querySelectorAll('[data-cart-count], [data-wishlist-count]');
    const badgeObserver = new MutationObserver(() => {
      badges.forEach((b) => { b.hidden = !(parseInt(b.textContent, 10) > 0); });
    });
    badges.forEach((b) => badgeObserver.observe(b, { childList: true, characterData: true, subtree: true }));

    /* ---- Sticky state + --header-height ----------------------------------------- */
    const setHeight = () => document.documentElement.style.setProperty('--header-height', `${sectionEl.offsetHeight}px`);
    setHeight();
    on(window, 'resize', setHeight);
    let stuckObserver = null;
    if (getComputedStyle(sectionEl).position === 'sticky') {
      stuckObserver = new IntersectionObserver(([entry]) => {
        sectionEl.classList.toggle('is-stuck', entry.intersectionRatio < 1 && entry.boundingClientRect.top <= 0);
      }, { threshold: [1], rootMargin: '-1px 0px 0px 0px' });
      stuckObserver.observe(sectionEl);
    }

    /* ---- Desktop/mobile switch cleanup ------------------------------------------ */
    on(window.matchMedia(DESKTOP), 'change', (e) => {
      if (e.matches) closeDrawer(false);
      else closeAll();
    });

    instances.set(id, {
      destroy() {
        controller.abort();
        badgeObserver.disconnect();
        if (resizeObserver) resizeObserver.disconnect();
        if (stuckObserver) stuckObserver.disconnect();
        disclosures.forEach((d) => clearTimeout(d.timer));
        closeDrawer(false);
        closeSearch();
        sectionEl.classList.remove('is-stuck');
      },
      // Theme Editor: reveal the block the merchant is editing.
      reveal(blockId) {
        const mega = disclosures.find((d) => d.panel.hasAttribute('data-hc-mega'));
        if (isDesktop()) {
          if (mega) open(mega);
          return;
        }
        openDrawer();
        const acc = drawer.querySelector(`[data-block-id="${CSS.escape(blockId || '')}"]`);
        const btn = acc && acc.querySelector('[data-hc-acc]');
        if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click();
        if (acc) acc.scrollIntoView({ block: 'nearest' });
      },
      conceal() {
        closeAll();
        closeDrawer(false);
      },
    });
  }

  function destroy(id) {
    const inst = instances.get(id);
    if (inst) { inst.destroy(); instances.delete(id); }
  }

  const boot = () => document.querySelectorAll('[data-hc-section]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const root = e.target.querySelector('[data-hc-section]');
      if (root) init(root);
    });
    document.addEventListener('shopify:section:unload', (e) => destroy(e.detail.sectionId));
    document.addEventListener('shopify:block:select', (e) => {
      const inst = instances.get(e.detail.sectionId);
      if (inst) inst.reveal(e.detail.blockId);
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      const inst = instances.get(e.detail.sectionId);
      if (inst) inst.conceal();
    });
  }
})();
